import { ErrorHandler, Injectable, Injector } from '@angular/core';
import * as Sentry from '@sentry/browser';

import { environment } from 'environments/environment';

import { ToastrService } from 'core/toastr.service';
import { getSafe, isNilOrEmpty } from 'helpers';

import { frontendVersion } from '../../generated/version';

function getEnvironmentFromLocation() {
  const host = getSafe(() => window.location.host, '');
  if (host.includes('actionbuilder.org')) {
    return 'production';
  }
  if (host.includes('weinteract.org')) {
    return 'staging';
  }
  if (host.includes('weinteractdev.org')) {
    return 'development';
  }
  return 'local';
}

if (environment.enableSentry) {
  Sentry.init({
    dsn: 'https://c5bbfb0f03c349cb8306edb811f6c146@sentry.io/1290165',
    attachStacktrace: true,
    logLevel: 1,
    integrations: integrations => integrations
      .filter(integration => integration.name !== 'TryCatch'),
    release: `action-builder-fe@${frontendVersion}`,
    beforeSend: (event: Sentry.Event) => {
      event.environment = getEnvironmentFromLocation();
      // event.user = {username: localStorage.getItem('uid')};
      return event;
    },
    beforeBreadcrumb: (breadcrumb: Sentry.Breadcrumb, hint?) => {
      if (breadcrumb.category === 'ui.click') {
        breadcrumb.message = `${breadcrumb.message} ${hint?.event?.target?.innerText}`;
      }
      return breadcrumb;
    }
  });
}

@Injectable(
  { providedIn: 'root' }
)
export class SentryErrorHandler implements ErrorHandler {

  /**
   *
   * @param injector It is important to know,
   * that the ErrorHandler service is part of the application initialisation phase of Angular.
   * In order to avoid a rather nasty cyclic dependency error,
   * we use the injector as its only constructor parameter.
   */
  constructor(private injector: Injector) {
  }

  handleError(error) {
    if (this.shouldIgnore(error)) {
      return;
    }

    Sentry.getCurrentHub().configureScope(scope => {
      scope.setUser({ username: localStorage.getItem('uid') });
      const errorType = error?.constructor?.name;
      if (errorType != null) {
        scope.setTag('error_type', errorType);
      }
    });

    if (environment.enableSentry) {
      Sentry.getCurrentHub().captureException(error);
    }

    this.injector.get(ToastrService).errorSkipLog(error);
  }

  private shouldIgnore(error: any) {
    if (this.isGraphQLError(error)) {
      return true;
    }

    const rejection = error['rejection'] || error;

    if (rejection) {
      const message = (rejection.message || '');
      if (rejection.status === 401 || message.includes('User is not logged in')) {
        // eslint-disable-next-line no-console
        console.info('User not logged in');
        return true;
      }
      // Ignore server errors, most of these are timeouts
      if (rejection.status === 504 || rejection.status === 0) {
        return true;
      }

      if (message.includes('Network error')) {
        // eslint-disable-next-line no-console
        console.info('Connectivity issue.');
        return true;
      }
    }

    return false;
  }

  private isGraphQLError(error: any) {
    return !isNilOrEmpty(error['graphQLErrors']);
  }
}
